/**
 * Usage Tracker — controls the free trial experience.
 *
 * Flow:
 *   Install → 24hr free autofill → soft nudge (5 more uses) → hard lock → sign up + subscribe
 *
 * Stored in localStorage so it persists across browser restarts but resets if the user
 * clears extension data (acceptable trade-off for simplicity).
 */

const USAGE_KEY = "jobily_usage";

// Config — tweak these as needed
const FREE_WINDOW_HOURS = 24;
const SOFT_NUDGE_USES = 5; // Extra free actions after the 24hr window expires

export interface UsageState {
  installedAt: string; // ISO timestamp of first launch
  actionCount: number; // Actions used AFTER the 24hr window expires
  setupComplete: boolean; // Has completed quick setup (name/email/phone)
}

export type AccessTier = "free" | "nudge" | "locked";

function getDefaultUsage(): UsageState {
  return {
    installedAt: new Date().toISOString(),
    actionCount: 0,
    setupComplete: false,
  };
}

export function loadUsage(): UsageState {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Ensure all fields exist (handles upgrades from older versions)
      return { ...getDefaultUsage(), ...parsed };
    }
  } catch {}
  // First launch — set the install timestamp
  const fresh = getDefaultUsage();
  saveUsage(fresh);
  return fresh;
}

export function saveUsage(usage: UsageState) {
  localStorage.setItem(USAGE_KEY, JSON.stringify(usage));
}

export function markSetupComplete() {
  const usage = loadUsage();
  usage.setupComplete = true;
  saveUsage(usage);
  return usage;
}

export function incrementActionCount(): UsageState {
  const usage = loadUsage();
  usage.actionCount += 1;
  saveUsage(usage);
  return usage;
}

/**
 * Determines the user's current access tier.
 *
 * - "free"   → within the 24hr window, everything works
 * - "nudge"  → 24hr expired, but they have remaining soft-nudge uses
 * - "locked" → all free uses exhausted, must sign up + subscribe
 */
export function getAccessTier(usage: UsageState): AccessTier {
  const installed = new Date(usage.installedAt).getTime();
  const now = Date.now();
  const hoursSinceInstall = (now - installed) / (1000 * 60 * 60);

  if (hoursSinceInstall < FREE_WINDOW_HOURS) {
    return "free";
  }

  if (usage.actionCount < SOFT_NUDGE_USES) {
    return "nudge";
  }

  return "locked";
}

/**
 * Returns info for the UI nudge banner.
 */
export function getNudgeInfo(usage: UsageState): {
  tier: AccessTier;
  remainingUses: number;
  hoursLeft: number;
} {
  const installed = new Date(usage.installedAt).getTime();
  const now = Date.now();
  const hoursLeft = Math.max(0, FREE_WINDOW_HOURS - (now - installed) / (1000 * 60 * 60));
  const tier = getAccessTier(usage);
  const remainingUses = tier === "nudge" ? Math.max(0, SOFT_NUDGE_USES - usage.actionCount) : 0;

  return { tier, remainingUses, hoursLeft };
}

